import { useAppSelector } from 'store/hooks';
import type {
  PayHubCashierContractsCashier3DSResultResponse,
  PayHubWidgetContractsSubmitPaymentResponse,
  PayHubWidgetContractsSubmitPayoutResponse,
} from 'store/models';

import type { ReservationHoldMessage } from '../api/reservationHold';
import { isAwaitingVerification } from '../api/reservationHold';
import { isReservationFlowActive } from './reservationFlags';
import { getReservationEntryPath, markReservationHold } from './reservationHold';

type SubmitResponse =
  | PayHubWidgetContractsSubmitPaymentResponse
  | PayHubWidgetContractsSubmitPayoutResponse
  | PayHubCashierContractsCashier3DSResultResponse;

/**
 * Secondary entry point into the reservation flow.
 *
 * The contract documents the hold on the `PaymentStatus` hub, which `ReservationHoldListener`
 * handles and which is the primary path. This covers the case where the same marker comes back on
 * the submit response itself — cheaper than a round trip through the hub, and it keeps the flow
 * reachable when the push channel is unavailable.
 *
 * Both paths navigate to the same idempotent destination, so a double signal is harmless: the
 * listener ignores anything raised while the player is already inside the flow, and the caller here
 * unmounts as soon as the navigation happens.
 *
 * Returns the path to redirect to, or `null` to let the standard status mapping proceed.
 */
export const useReservationRedirect = (response?: SubmitResponse): string | null => {
  const isActive = useAppSelector(isReservationFlowActive);

  if (!isActive || !isAwaitingVerification(response as ReservationHoldMessage | undefined)) {
    return null;
  }

  // Idempotent, so safe during render: lets `ReservationHoldGate` catch a later stale status redirect.
  markReservationHold();

  return getReservationEntryPath();
};
